import { dateTimeFormat } from "@common";

export const EMPTY_VALUE = "";

const formatArrayValue = (arr) => {
  if (!Array.isArray(arr) || arr.length === 0) return EMPTY_VALUE;
  return (
    arr
      .map((item) => {
        if (item === null || item === undefined) return "";
        if (typeof item === "object") {
          return (
            item?.industryName ||
            item?.domainName ||
            item?.productName ||
            item?.serviceName ||
            item?.name ||
            item?.label ||
            item?.value ||
            JSON.stringify(item)
          );
        }
        return String(item).trim();
      })
      .filter(Boolean)
      .join(", ") || EMPTY_VALUE
  );
};

export const formatObjectValue = (obj) => {
  if (!obj || typeof obj !== "object") return String(obj ?? "");
  const val =
    obj?.sourceName ||
    obj?.source ||
    obj?.inquirySource ||
    obj?.prospectSource ||
    obj?.otherSource ||
    obj?.mediumName ||
    obj?.medium ||
    obj?.industryName ||
    obj?.domainName ||
    obj?.productName ||
    obj?.serviceName ||
    obj?.currencyName ||
    obj?.currency ||
    obj?.priorityType ||
    obj?.leadType ||
    obj?.status ||
    obj?.name ||
    obj?.label ||
    obj?.value ||
    obj?.inquiryName ||
    obj?.leadName ||
    obj?.prospectName;

  if (typeof val === "object" && val !== null) {
    return formatObjectValue(val);
  }
  return val != null && val !== "" ? String(val).trim() : EMPTY_VALUE;
};

export const getValue = (value) => {
  if (value === null || value === undefined || value === "") return EMPTY_VALUE;

  if (Array.isArray(value)) {
    return formatArrayValue(value);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return formatArrayValue(parsed);
        }
      } catch {
        // Fall back
      }
    }
    return value;
  }

  if (typeof value === "object") {
    return formatObjectValue(value);
  }

  return String(value);
};
const getCurrecyValue = (currencyCode, currencyValue) => {
  return currencyValue ? `${currencyCode || ""} ${currencyValue || ""}` : ""
}
export const getBasicInformationView = (data = {}) => ({
  left: [
    { label: "Industry", value: getValue(data?.industry?.industryName || data?.industry) },
    { label: "Business Domain", value: getValue(data?.businessDomain) },
    { label: "Department", value: getValue(data?.department) },
    {
      label: "Interested Products",
      value: getValue(
        Array.isArray(data?.interestedProducts)
          ? data.interestedProducts
          : data?.interestedProducts
      ),
    },
    {
      label: "Interested Services",
      value: getValue(
        Array.isArray(data?.interestedServices)
          ? data.interestedServices
          : data?.interestedServices
      ),
    },
  ],
  right: [
    { label: "Deal Value", value: getCurrecyValue(data?.customerCurrency?.currencyCode, data?.estimatedDealValue) },
    // { label: "Priority", value: getValue(data?.priority) },
    { label: "Customer Profile", value: getValue(data?.idealCustomerProfile) },
  ],
  fullWidth: [
    { label: "Description", value: getValue(data?.description) },
    { label: "Note", value: getValue(data?.note) },
  ],
});

const resolveEntityName = (entity, keys = []) => {
  if (!entity) return "";
  if (typeof entity !== "object") return String(entity);
  for (const key of keys) {
    if (entity[key]) return entity[key];
  }
  return entity?.name || "";
};

const resolveContactName = (contact) => {
  if (!contact) return "";
  if (typeof contact !== "object") return String(contact);
  if (contact.fullName) return contact.fullName;
  if (contact.firstName) {
    return [contact.firstName, contact.lastName].filter(Boolean).join(" ");
  }
  return contact.contactName || contact.name || "";
};

const createClickHandler = (hasValue, handler, param) => {
  if (hasValue && handler) {
    return () => handler(param);
  }
  return undefined;
};

const hasDisplayValue = (val) => Boolean(val && val !== EMPTY_VALUE);

export const getLeadTaggingView = (data = {}, handlers = {}) => {
  const campDisplay = getValue(
    resolveEntityName(data?.campaign, ["campaignName", "name", "title"])
  );
  const prsDisplay = getValue(
    resolveEntityName(data?.prospect, ["prospectName", "name", "companyName"])
  );
  const prsContactDisplay = getValue(resolveContactName(data?.prospectContact));
  const inqDisplay = getValue(
    resolveEntityName(data?.inquiry, ["inquiryName", "inquiryNo", "name"])
  );
  const accountDisplay = getValue(
    resolveEntityName(data?.customerAccount || data?.account, [
      "customerAccountName",
      "accountName",
      "name",
    ])
  );
  const contactDisplay = getValue(resolveContactName(data?.contact));

  const hasCampaign = hasDisplayValue(campDisplay);
  const hasProspect = hasDisplayValue(prsDisplay);
  const hasProspectContact = hasDisplayValue(prsContactDisplay);
  const hasInquiry = hasDisplayValue(inqDisplay);

  return {
    left: [],
    right: [],
    fullWidth: [
      {
        label: "Campaign",
        value: campDisplay,
        onValueClick: createClickHandler(
          hasCampaign,
          handlers?.onCampaignClick,
          data?.campaign
        ),
      },
      {
        label: "Prospect",
        value: prsDisplay,
        onValueClick: createClickHandler(
          hasProspect,
          handlers?.onProspectClick,
          data?.prospect
        ),
      },
      {
        label: "Prospect Contact",
        value: prsContactDisplay,
        onValueClick: createClickHandler(
          hasProspectContact,
          handlers?.onProspectContactClick,
          data?.prospectContact
        ),
      },
      {
        label: "Inquiry",
        value: inqDisplay,
        onValueClick: createClickHandler(
          hasInquiry,
          handlers?.onInquiryClick,
          data?.inquiry
        ),
      },
      {
        label: "Account",
        value: accountDisplay,
      },
      {
        label: "Contact",
        value: contactDisplay,
      },
    ],
  };
};

export const getLeadQualificationView = (data = {}) => {
  const getLabel = (val, keys) => {
    if (!val) return EMPTY_VALUE;
    if (typeof val === "object") {
      for (const k of keys) {
        if (val[k]) return val[k];
      }
      return val?.name || val?.id || EMPTY_VALUE;
    }
    return String(val);
  };

  const getReasonLabel = (val) => {
    if (!val) return EMPTY_VALUE;
    if (typeof val === "object") {
      return (
        val?.disqualificationReason ||
        val?.nurtureReason ||
        val?.closureReason ||
        val?.reason ||
        val?.name ||
        EMPTY_VALUE
      );
    }
    return String(val);
  };

  const formatDateTime = (val) => {
    if (!val) return EMPTY_VALUE;
    try {
      return dateTimeFormat(val);
    } catch {
      return String(val);
    }
  };

  return {
    left: [
      {
        label: "Expected Timeline",
        value: dateTimeFormat(data?.expectedTimeline, true),
      },
      {
        label: "Intent Score",
        value: getValue(data?.buyingIntentScore),
      },
      {
        label: "Intent Level",
        value: getLabel(data?.buyingIntentLevel, [
          "intentLevelName",
          "intentLevel",
          "name",
        ]),
      },
      {
        label: "Qualification Maturity",
        value: getLabel(data?.qualificationMaturity, [
          "qualificationMaturity",
          "maturity",
          "name",
        ]),
      },
    ],
    right: [
      {
        label: "Qualification Readiness",
        value: getLabel(data?.qualificationReadiness, [
          "qualificationReadiness",
          "readiness",
          "name",
        ]),
      },
      {
        label: "Reason",
        value: getReasonLabel(data?.reason),
      },
      {
        label: "Qualification Disposition",
        value: getValue(data?.qualificationDisposition),
      },
    ],
    fullWidth: [
      {
        label: "Note",
        value: getValue(data?.note || ""),
      },
    ],
  };
};

export const getLeadSourceView = (data = {}, handlers = {}) => {
  const prsSrc = data?.prospectSource;
  const prsSrcValue =
    (typeof prsSrc === "object" && prsSrc !== null
      ? prsSrc?.prospectSource?.sourceName ||
      prsSrc?.prospectSource?.source ||
      prsSrc?.prospectSource?.name ||
      prsSrc?.prospectSource ||
      prsSrc?.sourceName ||
      prsSrc?.source ||
      prsSrc?.name
      : prsSrc) || "";

  const inqSrc = data?.inquirySource;
  const inqSrcValue =
    (typeof inqSrc === "object" && inqSrc !== null
      ? inqSrc?.inquirySource?.sourceName ||
      inqSrc?.inquirySource?.source ||
      inqSrc?.inquirySource?.name ||
      inqSrc?.inquirySource ||
      inqSrc?.sourceName ||
      inqSrc?.source ||
      inqSrc?.name
      : inqSrc) || "";

  const otherSrc = data?.otherSource;
  const otherSrcValue =
    (typeof otherSrc === "object" && otherSrc !== null
      ? otherSrc?.sourceName ||
      otherSrc?.source ||
      otherSrc?.name ||
      otherSrc?.label
      : otherSrc) || "";

  return {
    left: [],
    right: [],
    fullWidth: [
      {
        label: "Prospect Source",
        value: getValue(prsSrcValue),
        onValueClick: handlers?.onProspectSourceClick
          ? () => handlers.onProspectSourceClick(data?.prospectSource)
          : undefined,
      },
      {
        label: "Inquiry Source",
        value: getValue(inqSrcValue),
        onValueClick: handlers?.onInquirySourceClick
          ? () => handlers.onInquirySourceClick(data?.inquirySource)
          : undefined,
      },
      {
        label: "Other Source",
        value: getValue(otherSrcValue),
      },
      {
        label: "Note",
        value: getValue(data?.note || data?.notes || ""),
      },
    ],
  };
};


